/**
 * Vertrag als PDF (Befund 9): je Fassung (Agentur mit Anlage, Marke, Creator ohne verborgene Anlage) aus dem gerenderten
 * Vertragstext, mit Unterschriftsfeldern und Protokollseite. Schriften und Umbruch wie bei der Rechnung (invoice-pdf.ts);
 * Unterschriftsbilder als JPEG (die App wandelt das PNG der Unterschrift vorher um). Schutz gegen allein stehende
 * Unterschriftsfelder: Unterschriften bleiben zusammen und nehmen den letzten Absatz davor mit auf die Seite.
 * Reine Funktion ohne DOM, getestet in packages/domain/test/contract-pdf.test.ts.
 */
import { belegAkzent, pdfAccent, textWidth, winAnsiBytes } from "./invoice-pdf.js";
import { BELEG_SCHRIFT_FAKTOR, belegLayout, belegLogoBox } from "./beleg-layout.js";
import {
  CONTRACT_REF_PATTERN, CONTRACT_SUB_CHARS, CONTRACT_SUP_CHARS, contractCharClass, contractRefCodePoint, contractValueCharProblem, decodeContractRefs,
} from "./contract-markup.js";
import { CONTRACT_PDF_FONT } from "./contract-pdf-font.js";
import type { ContractRole } from "./contract-variables.js";
import type { Language } from "./types.js";

/** F1 normal, F2 fett, F3 kursiv, F4 fett kursiv (Helvetica-Familie; kursiv hat dieselben Breiten wie aufrecht) */
type FontKey = "F1" | "F2" | "F3" | "F4";
const metric = (f: FontKey): "F1" | "F2" => (f === "F2" || f === "F4" ? "F2" : "F1");
const fontOf = (bold: boolean, italic?: boolean): FontKey => (bold ? (italic ? "F4" : "F2") : italic ? "F3" : "F1");
const widthOf = (text: string, f: FontKey, size: number) => (needsUnicode(text) ? unicodeWidth(text, size) : textWidth(text, metric(f), size));
type Rgb = readonly [number, number, number];
const INK: Rgb = [0.125, 0.153, 0.141];
const MUTED: Rgb = [0.341, 0.396, 0.369];
const RULE: Rgb = [0.78, 0.8, 0.79];
const WARN: Rgb = [0.62, 0.36, 0.05];

const W = 595.28, H = 841.89, L = 62, R = W - 62, TOP = H - 64, BOTTOM = 70;
const WIDTH = R - L;

export interface ContractPdfImage {
  /** JPEG-Daten (DCT) */
  jpeg: Uint8Array;
  width: number;
  height: number;
}

export interface ContractPdfSignature {
  role: ContractRole;
  slot: number;
  /** Beschriftung, z. B. „Marke“ oder „Creator · gesetzliche Vertretung“ */
  label: string;
  signed: boolean;
  /** vorgesehene Person (vor der Unterschrift) */
  name?: string | null;
  signingName?: string | null;
  /** Funktion und Befugnis, schon lesbar zusammengesetzt */
  detail?: string | null;
  /** lesbarer Zeitpunkt der Unterschrift (in der Zeitzone der Agentur) */
  signedAt?: string | null;
  paper?: boolean;
  /** lesbares Datum auf dem Papier; signedAt ist dann der Zeitpunkt der Erfassung (VERTRAEGE-02) */
  paperSignedOn?: string | null;
  image?: ContractPdfImage | null;
}

export interface ContractPdfProtocol {
  title: string;
  intro: string;
  /** Zeilen wie „SHA-256 beim Versand: …“ */
  hashes: string[];
  columns: [string, string, string, string];
  rows: { at: string; event: string; person: string; evidence: string }[];
  how?: { title: string; text: string } | null;
}

/** Nachweise als Bild im Anhang (z. B. Screenshots im Bericht für die Marke): je Eintrag Titel, Zusatzzeilen und optional ein Bild */
export interface ContractPdfFigures {
  title: string;
  intro?: string | null;
  items: { caption: string; lines?: string[]; image?: ContractPdfImage | null }[];
}

export interface ContractPdfModel {
  language: Language;
  /** Dokumenttitel (Metadaten) */
  title: string;
  /** Kopfzeile jeder Seite, z. B. „Studio Nordlicht · V-0009 · Fassung für den Creator“ */
  header: string;
  /** Hinweis oben auf Seite 1 (etwa „Aufgehoben am …“) */
  banner?: string | null;
  /** Vermerk oben rechts auf jeder Seite (K-513: „Entwurf – nicht unterschrieben“ für Entwürfe und Vorlagen) */
  stamp?: string | null;
  /** Logo der Agentur als Dokumentkopf oben auf Seite 1 (höchstens 150 × 48 Punkt, wie auf Rechnung und Angebot) */
  logo?: ContractPdfImage | null;
  /** Agenturfarbe (org_settings.brand_color) für Kopfzeile und Linie darunter, wie auf Rechnung und Angebot; ohne: neutral */
  accent?: string | null;
  bodyHtml: string;
  /** null, wenn die Anlage für diese Fassung verborgen ist */
  annexHtml?: string | null;
  signatures: ContractPdfSignature[];
  /** Anhang mit Bildern nach dem Text, vor dem Protokoll (auf neuer Seite) */
  figures?: ContractPdfFigures | null;
  protocol?: ContractPdfProtocol | null;
  labels?: {
    page?: (n: number, of: number) => string; open?: string; signedOn?: string; paper?: string; paperOn?: string; recordedOn?: string;
    /** R-28: Satz unter einer elektronischen Unterschrift; der Nachweis-Teil folgt, wenn das Dokument ein Protokoll hat */
    electronic?: string; electronicProof?: (page: number) => string; electronicProofHere?: string;
  };
  /** PDF-Vorlage der Agentur für Verträge (nur Gestaltung; der Text kommt aus der Vertragsvorlage, beleg-layout.ts, 0456) */
  layout?: unknown;
  /** R-26 (Prüf-Chat 03.11.2026): Dokumentangaben für die PDF-Metadaten; Title bleibt `title`, Creator ist immer CreatorOffice */
  meta?: { author?: string | null; subject?: string | null; keywords?: string | null } | null;
}

const DEFAULT_LABELS: Record<Language, Required<NonNullable<ContractPdfModel["labels"]>>> = {
  de: { page: (a, b) => `Seite ${a} von ${b}`, open: "Noch nicht unterschrieben", signedOn: "unterschrieben am", paper: "auf Papier unterschrieben", paperOn: "auf Papier unterschrieben am", recordedOn: "erfasst am",
    electronic: "Elektronisch unterschrieben", electronicProof: (n) => `, Nachweis im Protokoll auf Seite ${n}`, electronicProofHere: ", Nachweis im Protokoll auf dieser Seite" },
  en: { page: (a, b) => `Page ${a} of ${b}`, open: "Not signed yet", signedOn: "signed on", paper: "signed on paper", paperOn: "signed on paper on", recordedOn: "recorded on",
    electronic: "Signed electronically", electronicProof: (n) => `, record in the signature log on page ${n}`, electronicProofHere: ", record in the signature log on this page" },
};

/** Platzhalter im Satz unter der Unterschrift; die Seitenzahl des Protokolls steht erst nach dem Umbruch fest (R-28) */
const NACHWEIS_PLATZHALTER = "#NACHWEIS#";

// ── PDF-Grundbausteine ───────────────────────────────────────────────────────────────────────────────────────────────

/** Unicode der WinAnsi-Bytes 0x80 … 0x9F (Umkehrung der Tabelle aus invoice-pdf.ts) für die Breitenliste der Schrift. */
const WIN_ANSI_HIGH: Record<number, number> = {
  0x80: 0x20ac, 0x82: 0x201a, 0x83: 0x0192, 0x84: 0x201e, 0x85: 0x2026, 0x86: 0x2020, 0x87: 0x2021, 0x88: 0x02c6, 0x89: 0x2030,
  0x8a: 0x0160, 0x8b: 0x2039, 0x8c: 0x0152, 0x8e: 0x017d, 0x91: 0x2018, 0x92: 0x2019, 0x93: 0x201c, 0x94: 0x201d, 0x95: 0x2022,
  0x96: 0x2013, 0x97: 0x2014, 0x98: 0x02dc, 0x99: 0x2122, 0x9a: 0x0161, 0x9b: 0x203a, 0x9c: 0x0153, 0x9e: 0x017e, 0x9f: 0x0178,
};
function fontWidths(font: FontKey): number[] {
  const out: number[] = [];
  for (let b = 32; b <= 255; b++) {
    const cp = b >= 0x80 && b <= 0x9f ? WIN_ANSI_HIGH[b] : b;
    out.push(cp ? Math.round(textWidth(String.fromCodePoint(cp), metric(font), 1000)) || 556 : 556);
  }
  return out;
}

// ── Unicode-Schrift (Prüfung 0–2, Befund 14) ─────────────────────────────────────────────────────────────────────────
// Text, der ganz in WinAnsi passt, steht wie bisher in Helvetica. Enthält ein Textstück ein Zeichen außerhalb von WinAnsi
// (Namen wie Dvořák, Şahin, Łukasz, Doğan, Erdős, kyrillische Buchstaben), steht das ganze Stück in der eingebetteten Teilmenge
// von Noto Sans (/FU, CIDFontType2, Identity-H, mit ToUnicode zum Kopieren und Suchen). Fett und kursiv werden dort
// nachgebildet (Kontur bzw. geneigte Grundlinie). Zeichen ohne Glyphe werden „?“ wie bisher.

let glyphMap: Map<number, number> | null = null;
function glyphOf(cp: number): number | undefined {
  if (!glyphMap) {
    glyphMap = new Map();
    for (const [a, b, g] of CONTRACT_PDF_FONT.cmap) for (let c = a; c <= b; c++) glyphMap.set(c, g + (c - a));
  }
  return glyphMap.get(cp);
}
const winAnsiOk = (ch: string) => ch === "?" || winAnsiBytes(ch)[0] !== 0x3f;

/** Braucht der Text die Unicode-Schrift (ein Zeichen außerhalb von WinAnsi)? */
export function needsUnicode(text: string): boolean {
  for (const ch of text.normalize("NFC")) if (!winAnsiOk(ch)) return true;
  return false;
}

/** Glyphen der Unicode-Schrift für einen Text: Leerraum wird Leerzeichen, fehlende Zeichen „?“. */
function glyphsOf(text: string): { gid: number; cp: number }[] {
  const out: { gid: number; cp: number }[] = [];
  for (const ch of text.normalize("NFC")) {
    let cp = ch.codePointAt(0)!;
    if (cp === 0x09 || cp === 0x0a || cp === 0x0d) cp = 0x20;
    let gid = contractCharClass(cp) ? undefined : glyphOf(cp);
    if (gid === undefined) { cp = 0x3f; gid = glyphOf(cp) ?? 0; }
    out.push({ gid, cp });
  }
  return out;
}

function unicodeWidth(text: string, size: number): number {
  return glyphsOf(text).reduce((s, g) => s + (CONTRACT_PDF_FONT.widths[g.gid] ?? 600), 0) * size / 1000;
}

/**
 * So steht ein Text im PDF: in WinAnsi darstellbare Zeichen und Zeichen mit Glyphe in der eingebetteten Unicode-Schrift wie
 * sie sind, alles andere „?“ (Steuerzeichen, fehlende Glyphen). Für Tests und die Gegenprobe angezeigt = gedruckt.
 */
export function contractPdfPrintedText(text: string): string {
  let out = "";
  for (const ch of text.normalize("NFC")) {
    const cp = ch.codePointAt(0)!;
    if (cp === 0x09 || cp === 0x0a || cp === 0x0d) out += " ";
    else if (winAnsiOk(ch) || (glyphOf(cp) !== undefined && !contractCharClass(cp))) out += ch;
    else out += "?";
  }
  return out;
}

let coverage: [number, number][] | null = null;
/**
 * Zeichen, die das Vertrags-PDF drucken kann: WinAnsi (Helvetica) und die Glyphen der eingebetteten Unicode-Schrift, ohne
 * Steuer- und unsichtbare Zeichen; dazu Tab, Zeilenumbruch, Seitenvorschub, Wagenrücklauf als Leerraum. Bereiche einschließlich,
 * aufsteigend; gleich app.contract_pdf_coverage() (Migration 0113, erweitert 0118; Gleichlauf-Test).
 */
export function contractPdfCoverage(): [number, number][] {
  if (coverage) return coverage;
  const ok = new Set<number>([0x09, 0x0a, 0x0c, 0x0d]);
  for (let cp = 0x20; cp <= 0x2fff; cp++) if (winAnsiOk(String.fromCodePoint(cp))) ok.add(cp);
  for (const [a, b] of CONTRACT_PDF_FONT.cmap) for (let cp = a; cp <= b; cp++) ok.add(cp);
  const sorted = [...ok].filter((cp) => cp <= 0x0d || !contractCharClass(cp)).sort((x, y) => x - y);
  const out: [number, number][] = [];
  for (const cp of sorted) {
    const last = out[out.length - 1];
    if (last && cp === last[1] + 1) last[1] = cp;
    else out.push([cp, cp]);
  }
  coverage = out;
  return out;
}
const printable = (cp: number) => contractPdfCoverage().some(([a, b]) => cp >= a && cp <= b);

/**
 * Erstes Zeichen des Vertragstexts, das das PDF nicht drucken kann (es stünde dort „?“), oder null. Geprüft wird der Text ohne
 * Auszeichnungen, danach jede Zeichenreferenz. Kennung `print:<Zeichen>` bzw. `print:<Referenz>`. Gleich app.contract_print_problem
 * (0113); contract_send sperrt damit (Befund 14: angezeigt = gedruckt, etwa Emoji oder Schriften ohne Glyphe).
 */
export function contractPrintProblem(html: string | null | undefined): string | null {
  if (!html) return null;
  const text = html.replace(/<[^>]*>/g, "").replace(new RegExp(CONTRACT_REF_PATTERN, "g"), "");
  for (const ch of text) if (!printable(ch.codePointAt(0)!)) return `print:${ch}`;
  if (html.includes("&")) {
    for (const m of html.matchAll(new RegExp(CONTRACT_REF_PATTERN, "g"))) {
      const cp = contractRefCodePoint(m[0]);
      if (cp !== null && !printable(cp)) return `print:${Array.from(m[0]).slice(0, 24).join("")}`;
    }
  }
  return null;
}

/**
 * Name einer Person (Unterzeichner, Name bei der Unterschrift): Steuer-, unsichtbare und nicht druckbare Zeichen sind nicht
 * erlaubt (Runde 2, R2-05: angezeigt = gedruckt, auch im Nachweis). Kennung `char:…` bzw. `print:…`; gleich app.contract_name_problem.
 */
export function contractNameProblem(name: string | null | undefined): string | null {
  if (!name) return null;
  return contractValueCharProblem(name) ?? contractPrintProblem(name.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;"));
}

function pdfString(text: string): string {
  let s = "(";
  for (const b of winAnsiBytes(text)) {
    if (b === 0x28 || b === 0x29 || b === 0x5c) s += `\\${String.fromCharCode(b)}`;
    else if (b < 0x20 || b > 0x7e) s += `\\${b.toString(8).padStart(3, "0")}`;
    else s += String.fromCharCode(b);
  }
  return `${s})`;
}
function pdfUnicode(text: string): string {
  let hex = "FEFF";
  for (let i = 0; i < text.length; i++) hex += text.charCodeAt(i).toString(16).padStart(4, "0").toUpperCase();
  return `<${hex}>`;
}
const num = (v: number) => (Math.round(v * 100) / 100).toString();

/**
 * Gezeichnetes auf einer Seite der Seitenansicht (PARITAET-R3-8): dieselben Koordinaten wie im PDF (Punkt, Ursprung unten
 * links), damit die Ansicht im Portal genau dort umbricht, wo das PDF umbricht.
 */
export type ContractPageItem =
  | { t: "text"; x: number; y: number; text: string; bold: boolean; italic: boolean; size: number; color: Rgb }
  | { t: "line"; x1: number; y1: number; x2: number; y2: number; width: number; color: Rgb }
  | { t: "image"; x: number; y: number; w: number; h: number; index: number };

class Page {
  ops: string[] = [];
  images = new Set<number>();
  items: ContractPageItem[] = [];
  /** benutzte Glyphen der Unicode-Schrift mit ihrem Zeichen (für /W und ToUnicode) */
  glyphs = new Map<number, number>();
  text(x: number, y: number, str: string, font: FontKey = "F1", size = 10, color: Rgb = INK) {
    if (!str) return;
    if (needsUnicode(str)) {
      const gs = glyphsOf(str);
      for (const g of gs) if (!this.glyphs.has(g.gid)) this.glyphs.set(g.gid, g.cp);
      const bold = font === "F2" || font === "F4";
      const italic = font === "F3" || font === "F4";
      const rgb = color.map(num).join(" ");
      const pos = italic ? `1 0 0.2126 1 ${num(x)} ${num(y)} Tm` : `${num(x)} ${num(y)} Td`;
      const hexGlyphs = gs.map((g) => g.gid.toString(16).padStart(4, "0").toUpperCase()).join("");
      this.ops.push(`BT /FU ${num(size)} Tf ${rgb} rg ${bold ? `${rgb} RG ${num(size * 0.035)} w 2 Tr ` : ""}${pos} <${hexGlyphs}> Tj${bold ? " 0 Tr" : ""} ET`);
    } else {
      this.ops.push(`BT /${font} ${num(size)} Tf ${color.map(num).join(" ")} rg ${num(x)} ${num(y)} Td ${pdfString(str)} Tj ET`);
    }
    this.items.push({ t: "text", x, y, text: str, bold: font === "F2" || font === "F4", italic: font === "F3" || font === "F4", size, color });
  }
  line(x1: number, y1: number, x2: number, y2: number, width = 0.6, color: Rgb = RULE) {
    this.ops.push(`${color.map(num).join(" ")} RG ${num(width)} w ${num(x1)} ${num(y1)} m ${num(x2)} ${num(y2)} l S`);
    this.items.push({ t: "line", x1, y1, x2, y2, width, color });
  }
  image(index: number, x: number, y: number, w: number, h: number) {
    this.images.add(index);
    this.ops.push(`q ${num(w)} 0 0 ${num(h)} ${num(x)} ${num(y)} cm /Im${index} Do Q`);
    this.items.push({ t: "image", x, y, w, h, index });
  }
}

function hex(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i++) {
    s += bytes[i]!.toString(16).padStart(2, "0");
    if (i % 60 === 59) s += "\n";
  }
  return `${s}>`;
}

const hexByte = (b: number) => b.toString(16).padStart(2, "0");
function asciiHex(bytes: ArrayLike<number>): string {
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += hexByte(bytes[i]!);
    if (i % 60 === 59) out += "\n";
  }
  return `${out}>`;
}
function base64Bytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
const utf16Hex = (cp: number) => (cp > 0xffff
  ? [0xd800 + ((cp - 0x10000) >> 10), 0xdc00 + ((cp - 0x10000) & 0x3ff)] : [cp]).map((u) => u.toString(16).padStart(4, "0").toUpperCase()).join("");

/** Objekte der eingebetteten Unicode-Schrift, nur wenn eine Seite sie benutzt; liefert die Nummer der Type0-Schrift. */
function unicodeFontObjects(pages: Page[], add: (body: string) => number): number | null {
  const used = new Map<number, number>();
  for (const p of pages) for (const [g, cp] of p.glyphs) if (!used.has(g)) used.set(g, cp);
  if (!used.size) return null;
  const f = CONTRACT_PDF_FONT;
  const base = `TKNOTO+${f.name}`;
  const file = asciiHex(base64Bytes(f.deflated));
  const fontFile = add(`<< /Length ${file.length} /Length1 ${f.length} /Filter [/ASCIIHexDecode /FlateDecode] >>\nstream\n${file}\nendstream`);
  const descriptor = add(`<< /Type /FontDescriptor /FontName /${base} /Flags 32 /FontBBox [${f.bbox.join(" ")}] /ItalicAngle 0 /Ascent ${f.ascent} /Descent ${f.descent} /CapHeight ${f.capHeight} /StemV 80 /FontFile2 ${fontFile} 0 R >>`);
  const gids = [...used.keys()].sort((a, b) => a - b);
  const w = gids.map((g) => `${g} [${f.widths[g] ?? 600}]`).join(" ");
  const cid = add(`<< /Type /Font /Subtype /CIDFontType2 /BaseFont /${base} /CIDSystemInfo << /Registry (Adobe) /Ordering (Identity) /Supplement 0 >> /FontDescriptor ${descriptor} 0 R /W [${w}] /CIDToGIDMap /Identity >>`);
  const chunks: string[] = [];
  for (let i = 0; i < gids.length; i += 100) {
    const part = gids.slice(i, i + 100);
    chunks.push(`${part.length} beginbfchar\n${part.map((g) => `<${g.toString(16).padStart(4, "0").toUpperCase()}> <${utf16Hex(used.get(g)!)}>`).join("\n")}\nendbfchar`);
  }
  const cmap = `/CIDInit /ProcSet findresource begin\n12 dict begin\nbegincmap\n/CIDSystemInfo << /Registry (Adobe) /Ordering (UCS) /Supplement 0 >> def\n/CMapName /Adobe-Identity-UCS def\n/CMapType 2 def\n1 begincodespacerange\n<0000> <FFFF>\nendcodespacerange\n${chunks.join("\n")}\nendcmap\nCMapName currentdict /CMap defineresource pop\nend\nend`;
  const cmapHex = asciiHex(Array.from(cmap, (c) => c.charCodeAt(0)));
  const toUnicode = add(`<< /Length ${cmapHex.length} /Filter /ASCIIHexDecode >>\nstream\n${cmapHex}\nendstream`);
  return add(`<< /Type /Font /Subtype /Type0 /BaseFont /${base} /Encoding /Identity-H /DescendantFonts [${cid} 0 R] /ToUnicode ${toUnicode} 0 R >>`);
}

function assemble(pages: Page[], images: ContractPdfImage[], title: string, created: Date, meta?: ContractPdfModel["meta"]): Uint8Array {
  const objs: string[] = [];
  const add = (body: string) => { objs.push(body); return objs.length; };
  const catalog = add("");
  const pagesObj = add("");
  const font = (base: string, key: FontKey) =>
    `<< /Type /Font /Subtype /Type1 /BaseFont /${base} /Encoding /WinAnsiEncoding /FirstChar 32 /LastChar 255 /Widths [${fontWidths(key).join(" ")}] >>`;
  const f1 = add(font("Helvetica", "F1"));
  const f2 = add(font("Helvetica-Bold", "F2"));
  const f3 = add(font("Helvetica-Oblique", "F3"));
  const f4 = add(font("Helvetica-BoldOblique", "F4"));
  const fu = unicodeFontObjects(pages, add);
  // Bilder als JPEG, hexkodiert: die Datei bleibt ASCII (einfacher Schreiber, Länge in Zeichen = Länge in Bytes)
  const imageObjs = images.map((img) => {
    const data = hex(img.jpeg);
    return add(`<< /Type /XObject /Subtype /Image /Width ${img.width} /Height ${img.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter [/ASCIIHexDecode /DCTDecode] /Length ${data.length} >>\nstream\n${data}\nendstream`);
  });
  const kids: number[] = [];
  for (const p of pages) {
    const stream = p.ops.join("\n");
    const content = add(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
    const xo = [...p.images].map((i) => `/Im${i} ${imageObjs[i]} 0 R`).join(" ");
    kids.push(add(`<< /Type /Page /Parent ${pagesObj} 0 R /MediaBox [0 0 ${W} ${H}] /Resources << /Font << /F1 ${f1} 0 R /F2 ${f2} 0 R /F3 ${f3} 0 R /F4 ${f4} 0 R${fu ? ` /FU ${fu} 0 R` : ""} >>${xo ? ` /XObject << ${xo} >>` : ""} >> /Contents ${content} 0 R >>`));
  }
  objs[catalog - 1] = `<< /Type /Catalog /Pages ${pagesObj} 0 R >>`;
  objs[pagesObj - 1] = `<< /Type /Pages /Kids [${kids.map((k) => `${k} 0 R`).join(" ")}] /Count ${kids.length} >>`;
  const d = created.toISOString().replace(/[-:T]/g, "").slice(0, 14);
  // R-26: Author (Agentur), Subject (Nummer und Fassung), Keywords (Nummer, Titel, Namen); Creator und Producer CreatorOffice
  const feld = (name: string, wert: string | null | undefined) => (wert?.trim() ? ` /${name} ${pdfUnicode(wert.trim())}` : "");
  const info = add(`<< /Title ${pdfUnicode(title)}${feld("Author", meta?.author)}${feld("Subject", meta?.subject)}${feld("Keywords", meta?.keywords)} /Creator (CreatorOffice) /Producer (CreatorOffice) /CreationDate (D:${d}Z) >>`);
  const header = [..."%PDF-1.4\n%"].map((c) => c.charCodeAt(0)).concat([0xe2, 0xe3, 0xcf, 0xd3, 0x0a]);
  let offset = header.length;
  let out = "";
  const offsets: number[] = [];
  objs.forEach((body, i) => {
    offsets.push(offset);
    const chunk = `${i + 1} 0 obj\n${body}\nendobj\n`;
    out += chunk;
    offset += chunk.length;
  });
  const xref = offset;
  out += `xref\n0 ${objs.length + 1}\n0000000000 65535 f \n${offsets.map((o) => `${String(o).padStart(10, "0")} 00000 n \n`).join("")}`;
  out += `trailer\n<< /Size ${objs.length + 1} /Root ${catalog} 0 R /Info ${info} 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
  const all = new Uint8Array(header.length + out.length);
  all.set(header, 0);
  for (let i = 0; i < out.length; i++) all[header.length + i] = out.charCodeAt(i) & 0xff;
  return all;
}

// ── HTML des Vertrags in Blöcke ───────────────────────────────────────────────────────────────────────────────────────

export interface Run { text: string; bold: boolean; italic?: boolean; underline?: boolean }
export type PdfAlign = "center" | "right";
export type PdfBlock =
  | { kind: "h1" | "h2" | "h3" | "p"; runs: Run[]; align?: PdfAlign }
  | { kind: "li"; runs: Run[]; marker: string; depth: number }
  | { kind: "signature"; role: ContractRole }
  /** erzwungener Seitenumbruch des Editors (<div class="tk-page-break">, PARITAET-R3-8) */
  | { kind: "pagebreak" }
  /** Tabelle (Vertragsbaukasten Schritt 6, VB-24): Zeilen mit Zellen im Raster; Kopfzeilen (thead oder nur th) wiederholen sich */
  | { kind: "table"; rows: PdfTableRow[] };

/** Zelle im Raster: Spalte, Breite in Spalten (colspan), Kopfzelle (th, fett) */
export interface PdfTableCell { runs: Run[]; col: number; span: number; header: boolean }
export interface PdfTableRow { header: boolean; cells: PdfTableCell[] }

/**
 * Zeichenreferenzen wie der Browser (Befunde 15, 16): numerisch mit und ohne Semikolon (ungültige werden U+FFFD, nie ein
 * Absturz), benannt nach der Liste der Positivliste (CONTRACT_NAMED_REFS in contract-markup.ts).
 */
const decode = decodeContractRefs;

/**
 * Zerlegt den gerenderten (bereinigten) Vertragstext in Überschriften, Absätze, Listenpunkte, Tabellen und Unterschriftsfelder.
 * Unbekannte Auszeichnungen werden Text. In einer Tabelle sammelt jede Zelle ihren Text (Absätze und br als Zeilenumbruch).
 */
export function contractBlocks(html: string): PdfBlock[] {
  const out: PdfBlock[] = [];
  let runs: Run[] = [];
  let kind: "h1" | "h2" | "h3" | "p" | "li" = "p";
  let bold = 0;
  let italic = 0;
  let underline = 0;
  let align: PdfAlign | undefined;
  const lists: { ordered: boolean; n: number }[] = [];
  // Runde 2, R2-10b: Listenpunkt offen? Ein Absatz direkt in ol/ul (nicht in li) hat keine Nummer, wie am Bildschirm
  const inItem: boolean[] = [];
  // Runde 2, R2-09: Hoch- und Tiefstellung als hoch- bzw. tiefgestellte Zeichen (die Positivliste lässt nur solche zu)
  let script: "sup" | "sub" | null = null;
  const scripted = (text: string) => {
    if (!script) return text;
    const map = script === "sup" ? CONTRACT_SUP_CHARS : CONTRACT_SUB_CHARS;
    return Array.from(text, (ch) => (Object.hasOwn(map, ch) ? map[ch]! : ch)).join("");
  };
  const run = (text: string): Run => ({ text: scripted(text), bold: bold > 0, ...(italic > 0 ? { italic: true } : {}), ...(underline > 0 ? { underline: true } : {}) });
  let marker = "";
  // Tabelle (Schritt 6): Stapel offener Tabellen (verschachtelte Tabellen werden Text in ihrer Zelle, Befund 3)
  interface OffeneZelle { runs: Run[]; header: boolean; colspan: number; rowspan: number; lists: { ordered: boolean; n: number }[] }
  interface OffeneTabelle { rows: { cells: OffeneZelle[]; inHead: boolean }[]; row: { cells: OffeneZelle[]; inHead: boolean } | null; cell: OffeneZelle | null; inHead: boolean; caption: Run[] | null; inCaption: boolean }
  const tables: OffeneTabelle[] = [];
  const cleanRuns = (rs: Run[]): Run[] => {
    const clean = rs.map((r) => ({ ...r, text: r.text.replace(/[ \t\r]+/g, " ").replace(/ ?\n ?/g, "\n").replace(/\n{2,}/g, "\n") }));
    if (clean[0]) clean[0].text = clean[0].text.replace(/^[ \n]+/, "");
    const last = clean[clean.length - 1];
    if (last) last.text = last.text.replace(/[ \n]+$/, "");
    return clean.filter((r) => r.text !== "");
  };
  const zellText = (c: OffeneZelle) => c.runs.map((r) => r.text).join("");
  /** Zeilenumbruch in der Zelle, wenn schon Text da ist und nicht gerade einer steht */
  const umbruch = (c: OffeneZelle) => { const tx = zellText(c); if (tx.trim() && !/\n\s*$/.test(tx)) c.runs.push({ text: "\n", bold: false }); };
  const spanOf = (attrs: string, name: string) => Math.max(1, Math.min(20, Number(new RegExp(`${name}="(\\d+)"`).exec(attrs)?.[1] ?? 1) || 1));
  const zeileZu = (tb: OffeneTabelle) => {
    if (tb.cell && tb.row) { tb.row.cells.push(tb.cell); tb.cell = null; }
    if (tb.row && tb.row.cells.length) tb.rows.push(tb.row);
    tb.row = null;
  };
  /** Tabelle abschließen: Raster mit colspan/rowspan, Kopfzeile = thead oder eine Zeile nur aus th (Befund 2) */
  const tabelleZu = (tb: OffeneTabelle): { caption: Run[] | null; rows: PdfTableRow[] } => {
    zeileZu(tb);
    const belegt: number[] = []; // je Spalte: wie viele Zeilen noch durch rowspan belegt
    const rows: PdfTableRow[] = tb.rows.map((r) => {
      let col = 0;
      const cells: PdfTableCell[] = [];
      for (const c of r.cells) {
        while ((belegt[col] ?? 0) > 0) col++;
        cells.push({ runs: cleanRuns(c.runs), col, span: c.colspan, header: c.header });
        for (let k = 0; k < c.colspan; k++) belegt[col + k] = Math.max(belegt[col + k] ?? 0, c.rowspan);
        col += c.colspan;
      }
      for (let k = 0; k < belegt.length; k++) if ((belegt[k] ?? 0) > 0) belegt[k]! -= 1;
      return { header: r.inHead || (cells.length > 0 && cells.every((c) => c.header)), cells };
    });
    return { caption: tb.caption ? cleanRuns(tb.caption) : null, rows };
  };
  const tabelleAus = (tb: OffeneTabelle) => {
    const t = tabelleZu(tb);
    if (t.caption?.length) out.push({ kind: "p", runs: t.caption, align: "center" });
    if (t.rows.length) out.push({ kind: "table", rows: t.rows });
  };
  /** innere Tabelle als Text in die Zelle der äußeren: Zellen mit „ | “, Zeilen untereinander */
  const tabelleAlsText = (tb: OffeneTabelle, ziel: OffeneZelle) => {
    const t = tabelleZu(tb);
    umbruch(ziel);
    if (t.caption?.length) { ziel.runs.push(...t.caption); ziel.runs.push({ text: "\n", bold: false }); }
    t.rows.forEach((r, i) => {
      r.cells.forEach((c, k) => { if (k) ziel.runs.push({ text: " | ", bold: false }); ziel.runs.push(...(c.header ? c.runs.map((x) => ({ ...x, bold: true })) : c.runs)); });
      if (i < t.rows.length - 1) ziel.runs.push({ text: "\n", bold: false });
    });
    ziel.runs.push({ text: "\n", bold: false });
  };
  const flush = () => {
    const text = runs.map((r) => r.text).join("");
    if (text.replace(/[\s ]/g, "") !== "") {
      // Leerraum am Rand und doppelte Leerzeichen entfernen (Umbrüche bleiben)
      const clean = runs.map((r) => ({ ...r, text: r.text.replace(/[ \t\r]+/g, " ") }));
      if (clean[0]) clean[0].text = clean[0].text.replace(/^[ \n]+/, "");
      const last = clean[clean.length - 1];
      if (last) last.text = last.text.replace(/[ \n]+$/, "");
      if (kind === "li") out.push({ kind: "li", runs: clean, marker, depth: Math.max(0, lists.length - 1) });
      else out.push(align ? { kind, runs: clean, align } : { kind, runs: clean });
    }
    runs = [];
  };
  const re = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)([^>]*)>|([^<]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    if (m[4] !== undefined) {
      const t = decode(m[4]).replace(/\s*\n\s*/g, " ");
      const tb = tables[tables.length - 1];
      if (t && tb) { if (tb.cell) tb.cell.runs.push(run(t)); else if (tb.inCaption) (tb.caption ??= []).push(run(t)); else if (t.trim()) { tb.row ??= { cells: [], inHead: tb.inHead }; tb.cell = { runs: [run(t)], header: false, colspan: 1, rowspan: 1, lists: [] }; } continue; }
      if (t) runs.push(run(t));
      continue;
    }
    const close = m[1] === "/";
    const tag = m[2]!.toLowerCase();
    const attrs = m[3] ?? "";
    // In einer Tabelle: Zeilen und Zellen sammeln; Blöcke in einer Zelle werden Zeilenumbrüche, Listenpunkte mit Zeichen
    if (tag === "table") {
      if (!close) {
        if (!tables.length) flush();
        tables.push({ rows: [], row: null, cell: null, inHead: false, caption: null, inCaption: false });
      } else if (tables.length) {
        const tb = tables.pop()!;
        const aussen = tables[tables.length - 1];
        if (aussen) { aussen.row ??= { cells: [], inHead: aussen.inHead }; aussen.cell ??= { runs: [], header: false, colspan: 1, rowspan: 1, lists: [] }; tabelleAlsText(tb, aussen.cell); }
        else tabelleAus(tb);
      }
      continue;
    }
    const tb = tables[tables.length - 1];
    if (tb) {
      const c = tb.cell;
      switch (tag) {
        case "caption": tb.inCaption = !close; if (!close) tb.caption ??= []; break;
        case "thead": tb.inHead = !close; break;
        case "tbody": case "tfoot": if (!close) tb.inHead = false; break;
        case "tr":
          zeileZu(tb);
          if (!close) tb.row = { cells: [], inHead: tb.inHead };
          break;
        case "td": case "th":
          if (!close) {
            if (tb.cell && tb.row) { tb.row.cells.push(tb.cell); }
            tb.row ??= { cells: [], inHead: tb.inHead };
            tb.cell = { runs: [], header: tag === "th", colspan: spanOf(attrs, "colspan"), rowspan: spanOf(attrs, "rowspan"), lists: [] };
          } else if (tb.cell && tb.row) { tb.row.cells.push(tb.cell); tb.cell = null; }
          break;
        case "br": if (c) c.runs.push(run("\n")); break;
        case "p": case "div": case "h1": case "h2": case "h3": case "h4": case "blockquote": case "section": case "article": case "hr": case "dl": case "dt": case "dd":
          if (c) umbruch(c);
          break;
        case "ul": case "ol":
          if (c) { umbruch(c); if (close) c.lists.pop(); else c.lists.push({ ordered: tag === "ol", n: 0 }); }
          break;
        case "li":
          if (c) {
            umbruch(c);
            if (!close) { const l = c.lists[c.lists.length - 1]; if (l) l.n += 1; c.runs.push({ text: l?.ordered ? `${l.n}. ` : "• ", bold: false }); }
          }
          break;
        case "strong": case "b": bold = Math.max(0, bold + (close ? -1 : 1)); break;
        case "em": case "i": italic = Math.max(0, italic + (close ? -1 : 1)); break;
        case "u": underline = Math.max(0, underline + (close ? -1 : 1)); break;
        case "sup": case "sub": script = close ? null : tag; break;
        default: break;
      }
      continue;
    }
    switch (tag) {
      case "h1": case "h2": case "h3": case "h4":
        flush();
        kind = close ? "p" : (tag === "h4" ? "h3" : tag);
        align = close ? undefined : alignOf(attrs);
        break;
      case "p": case "div": case "section": case "article": case "blockquote": case "tr":
        if (!close && tag === "div" && /class="tk-signature"/.test(attrs)) {
          flush();
          const role = /data-role="([a-z]+)"/.exec(attrs)?.[1];
          if (role === "agency" || role === "brand" || role === "talent") out.push({ kind: "signature", role });
          break;
        }
        if (!close && tag === "div" && /class="[^"]*\btk-page-break\b/.test(attrs)) {
          flush();
          out.push({ kind: "pagebreak" });
          break;
        }
        flush();
        if (!lists.length || !inItem[inItem.length - 1]) kind = "p";
        else if (close) kind = "li";
        align = !close && tag === "p" && kind === "p" ? alignOf(attrs) : undefined;
        break;
      case "td": case "th":
        if (close) runs.push({ text: "  ", bold: false });
        break;
      case "br":
        runs.push(run("\n"));
        break;
      case "strong": case "b":
        bold = Math.max(0, bold + (close ? -1 : 1));
        break;
      case "em": case "i":
        italic = Math.max(0, italic + (close ? -1 : 1));
        break;
      case "u":
        underline = Math.max(0, underline + (close ? -1 : 1));
        break;
      case "sup": case "sub":
        script = close ? null : tag;
        break;
      case "ol": case "ul":
        flush();
        if (close) { lists.pop(); inItem.pop(); } else { lists.push({ ordered: tag === "ol", n: 0 }); inItem.push(false); }
        kind = lists.length && inItem[inItem.length - 1] ? "li" : "p";
        break;
      case "li":
        flush();
        if (!close) {
          const l = lists[lists.length - 1];
          if (l) l.n += 1;
          marker = l?.ordered ? `${l.n}.` : "•";
          kind = "li";
          if (inItem.length) inItem[inItem.length - 1] = true;
        } else {
          if (inItem.length) inItem[inItem.length - 1] = false;
          kind = "p";
        }
        break;
      default:
        break;
    }
  }
  flush();
  // Nicht geschlossene Tabellen (die Positivliste lässt sie zu): Inhalt nicht verlieren (Befund 9)
  while (tables.length) {
    const tb = tables.pop()!;
    const aussen = tables[tables.length - 1];
    if (aussen) { aussen.cell ??= { runs: [], header: false, colspan: 1, rowspan: 1, lists: [] }; aussen.row ??= { cells: [], inHead: aussen.inHead }; tabelleAlsText(tb, aussen.cell); }
    else tabelleAus(tb);
  }
  return out;
}

function alignOf(attrs: string): PdfAlign | undefined {
  const cls = /class="([^"]*)"/.exec(attrs)?.[1] ?? "";
  return /\btk-align-center\b/.test(cls) ? "center" : /\btk-align-right\b/.test(cls) ? "right" : undefined;
}

// ── Satz ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

interface Word { text: string; bold: boolean; italic: boolean; underline: boolean; space: boolean; br: boolean }
function words(runs: Run[]): Word[] {
  const out: Word[] = [];
  for (const r of runs) {
    const parts = r.text.split(/(\n| +)/);
    const style = { bold: r.bold, italic: !!r.italic, underline: !!r.underline };
    for (const p of parts) {
      if (p === "") continue;
      if (p === "\n") { out.push({ text: "", ...style, space: false, br: true }); continue; }
      if (/^ +$/.test(p)) { if (out.length) out[out.length - 1]!.space = true; continue; }
      out.push({ text: p, ...style, space: false, br: false });
    }
  }
  return out;
}
export type LinePart = { text: string; bold: boolean; italic?: boolean; underline?: boolean; x: number };
type Line = { parts: LinePart[] };
const sameStyle = (a: { bold: boolean; italic?: boolean; underline?: boolean }, b: { bold: boolean; italic?: boolean; underline?: boolean }) =>
  a.bold === b.bold && !!a.italic === !!b.italic && !!a.underline === !!b.underline;
const partOf = (w: Word, text: string, x: number): LinePart =>
  ({ text, bold: w.bold, ...(w.italic ? { italic: true } : {}), ...(w.underline ? { underline: true } : {}), x });
/** Absatz mit fetten Stellen auf eine Breite umbrechen. */
export function layoutRuns(runs: Run[], size: number, maxWidth: number): Line[] {
  const lines: Line[] = [];
  let cur: Line = { parts: [] };
  let x = 0;
  const space = (b: boolean) => widthOf(" ", b ? "F2" : "F1", size);
  const push = () => { lines.push(cur); cur = { parts: [] }; x = 0; };
  const ws = words(runs);
  ws.forEach((w, i) => {
    if (w.br) { push(); return; }
    const font: FontKey = fontOf(w.bold, w.italic);
    let text = w.text;
    let width = widthOf(text, font, size);
    if (x > 0 && x + width > maxWidth) push();
    maxWidth = Math.max(1, maxWidth);
    while (text && width > maxWidth) {
      // überlange Zeichenfolge (etwa eine Prüfsumme): hart teilen
      let part = "";
      for (const ch of text) { if (widthOf(part + ch, font, size) > maxWidth - x && part) break; part += ch; }
      cur.parts.push(partOf(w, part, x));
      push();
      text = text.slice(part.length);
      width = widthOf(text, font, size);
    }
    // Wörter gleicher Schrift in einem Textstück (kleinere Datei, Text lässt sich am Stück kopieren und suchen)
    const last = cur.parts[cur.parts.length - 1];
    const gap = i > 0 && ws[i - 1]!.space && !ws[i - 1]!.br;
    // nur Stücke derselben Schrift zusammenlegen (Helvetica bzw. Unicode-Schrift, Befund 14)
    if (last && sameStyle(last, w) && gap && needsUnicode(last.text) === needsUnicode(text)
      && Math.abs(last.x + widthOf(`${last.text} `, font, size) - x) < 0.01) last.text += ` ${text}`;
    else cur.parts.push(partOf(w, text, x));
    x += width + (w.space && i < ws.length - 1 ? space(w.bold) : 0);
  });
  if (cur.parts.length || !lines.length) push();
  return lines;
}

interface Box {
  h: number; keep: boolean; heading?: boolean; pageBreakBefore?: boolean; draw: (p: Page, top: number) => void;
  /** Tabellenzeile: Kopfzeilen, die auf einer neuen Seite vor dieser Zeile wiederholt werden */
  repeat?: Box[];
  /** Tabellenzeile ohne Kopfzeile: auf einer neuen Seite eine obere Rahmenlinie ziehen (Befund 12) */
  lineOnBreak?: boolean;
  /** Abstand ohne Inhalt (etwa nach einer Tabelle): zählt beim Zusammenhalten vor Unterschriften nicht (Befund 10) */
  spacer?: boolean;
}

const CELL = { size: 9, lead: 12, padX: 5, padY: 4 } as const;
/** schmalste Spalte: Rand plus mindestens zwei Zeichen (sonst endloser Umbruch, Befund 8) */
const MIN_COL = 2 * CELL.padX + 12;

/**
 * Tabelle als Kästen je Zeile (Schritt 6, VB-24): Raster mit colspan/rowspan, Spaltenbreiten nach dem Inhalt (mindestens
 * MIN_COL, zusammen der Satzspiegel; bei sehr vielen Spalten kleinere Schrift), Umbruch je Zelle, Rahmenlinien; nur th-Zellen
 * fett; Kopfzeilen auf jeder Folgeseite wiederholt. Eine Zeile höher als die Seite wird in Teilzeilen geteilt (Befund 1).
 */
function tableBoxes(b: Extract<PdfBlock, { kind: "table" }>): Box[] {
  const ncol = Math.max(1, ...b.rows.map((r) => Math.max(0, ...r.cells.map((c) => c.col + c.span))));
  // sehr viele Spalten: kleinere Schrift, damit jede Spalte mindestens MIN_COL breit bleibt
  const size = ncol * MIN_COL > WIDTH ? Math.max(5, CELL.size * WIDTH / (ncol * MIN_COL)) : CELL.size;
  const lead = CELL.lead * (size / CELL.size);
  const minCol = Math.min(MIN_COL, WIDTH / ncol);
  const natural = Array.from({ length: ncol }, () => minCol);
  for (const r of b.rows) for (const c of r.cells) {
    if (c.span !== 1) continue;
    const lines = c.runs.map((x) => x.text).join("").split("\n");
    const w = Math.max(0, ...lines.map((ln) => widthOf(ln, fontOf(c.header || c.runs.some((x) => x.bold), false), size))) + 2 * CELL.padX;
    natural[c.col] = Math.max(natural[c.col]!, w);
  }
  const total = natural.reduce((a, x) => a + x, 0);
  let widths: number[];
  if (total <= WIDTH) widths = natural.map((w) => w + (WIDTH - total) / ncol);
  else {
    // Spalten, die in ihren Anteil passen, behalten ihre natürliche Breite; der Rest geht anteilig an die breiten Spalten
    widths = Array.from({ length: ncol }, () => 0);
    let offen = natural.map((_, i) => i);
    let rest = WIDTH;
    for (;;) {
      const anteil = rest / offen.length;
      const passt = offen.filter((i) => natural[i]! <= anteil);
      if (!passt.length) break;
      for (const i of passt) { widths[i] = natural[i]!; rest -= natural[i]!; }
      offen = offen.filter((i) => !passt.includes(i));
      if (!offen.length) break;
    }
    const summe = offen.reduce((a, i) => a + natural[i]!, 0);
    for (const i of offen) widths[i] = Math.max(minCol, (rest * natural[i]!) / summe);
  }
  const sum = widths.reduce((a, x) => a + x, 0);
  widths = widths.map((w) => (w / sum) * WIDTH);
  const xs = widths.reduce<number[]>((acc, w, i) => [...acc, (acc[i] ?? 0) + w], [0]);
  const breite = (c: PdfTableCell) => xs[Math.min(ncol, c.col + c.span)]! - xs[c.col]!;
  const kopfH = (rows: PdfTableRow[]) => rows.reduce((a, r) => a + rowHeight(r), 0);
  function wrap(r: PdfTableRow) {
    return r.cells.map((c) => {
      const runs = c.header ? c.runs.map((x) => ({ ...x, bold: true })) : c.runs;
      return runs.length ? layoutRuns(runs, size, Math.max(1, breite(c) - 2 * CELL.padX)) : [];
    });
  }
  function rowHeight(r: PdfTableRow) { return Math.max(1, ...wrap(r).map((w) => w.length)) * lead + 2 * CELL.padY; }
  const kopfZeilen = [];
  for (const r of b.rows) { if (r.header) kopfZeilen.push(r); else break; }
  const platz = TOP - BOTTOM - kopfH(kopfZeilen) - 2 * CELL.padY;
  const maxZeilen = Math.max(1, Math.floor(platz / lead));

  /** Kästen einer Zeile: bei Überhöhe in Stücken zu maxZeilen Zeilen */
  const rowBoxes = (r: PdfTableRow, first: boolean): Box[] => {
    const wrapped = wrap(r);
    const n = Math.max(1, ...wrapped.map((w) => w.length));
    const stuecke = Math.ceil(n / maxZeilen);
    return Array.from({ length: stuecke }, (_, k) => {
      const von = k * maxZeilen, bis = Math.min(n, von + maxZeilen);
      const h = (bis - von) * lead + 2 * CELL.padY;
      return {
        h, keep: r.header, lineOnBreak: !r.header,
        draw: (p: Page, top: number) => {
          if ((first && k === 0) || r.header) p.line(L, top, R, top, r.header ? 0.8 : 0.5);
          r.cells.forEach((c, ci) => wrapped[ci]!.slice(von, bis).forEach((ln, li) => {
            for (const part of ln.parts) drawPart(p, L + xs[c.col]! + CELL.padX + part.x, top - CELL.padY - size - li * lead + 1.5, part, size);
          }));
          p.line(L, top - h, R, top - h, r.header ? 0.8 : 0.5);
          // senkrechte Linien: äußere Ränder und Zellgrenzen dieser Zeile (verbundene Zellen ohne Linie dazwischen)
          const grenzen = new Set<number>([0, ncol, ...r.cells.flatMap((c) => [c.col, Math.min(ncol, c.col + c.span)])]);
          for (const g of grenzen) p.line(L + xs[g]!, top, L + xs[g]!, top - h, 0.5);
        },
      };
    });
  };
  const out: Box[] = [];
  const kopf: Box[] = [];
  let nurKopf = true;
  b.rows.forEach((r, i) => {
    const boxes = rowBoxes(r, i === 0);
    if (r.header && nurKopf) kopf.push(...rowBoxes(r, true));
    else { nurKopf = false; if (kopf.length) for (const x of boxes) x.repeat = kopf; }
    out.push(...boxes);
  });
  // Abstand nach der Tabelle
  out.push({ h: 8, keep: false, spacer: true, draw: () => undefined });
  return out;
}

/** Textstück zeichnen: Schrift nach fett/kursiv, unterstrichen mit einer feinen Linie unter der Grundlinie. */
function drawPart(p: Page, x: number, base: number, part: LinePart, size: number, color: Rgb = INK) {
  const font = fontOf(part.bold, part.italic);
  p.text(x, base, part.text, font, size, color);
  if (part.underline) p.line(x, base - size * 0.15, x + widthOf(part.text, font, size), base - size * 0.15, Math.max(0.4, size * 0.05), color);
}

const STYLE: Record<"h1" | "h2" | "h3" | "p" | "li", { size: number; lead: number; before: number; after: number; bold: boolean }> = {
  h1: { size: 16, lead: 20, before: 0, after: 8, bold: true },
  h2: { size: 11.5, lead: 15, before: 10, after: 3, bold: true },
  h3: { size: 10.5, lead: 14, before: 6, after: 2, bold: true },
  p: { size: 10, lead: 13.6, before: 0, after: 6, bold: false },
  li: { size: 10, lead: 13.6, before: 0, after: 4, bold: false },
};

/** Schriftgröße des Vertragstexts nach PDF-Vorlage (für die Dauer eines Layouts gesetzt) */
let textSkala = 1;

function textBoxes(b: Extract<PdfBlock, { kind: "h1" | "h2" | "h3" | "p" | "li" }>): Box[] {
  const st0 = STYLE[b.kind];
  const st = textSkala === 1 ? st0 : { ...st0, size: st0.size * textSkala, lead: st0.lead * textSkala };
  const indent = b.kind === "li" ? 18 + b.depth * 16 : 0;
  const runs = st.bold ? b.runs.map((r) => ({ ...r, bold: true })) : b.runs;
  const lines = layoutRuns(runs, st.size, WIDTH - indent);
  const align = b.kind === "li" ? undefined : b.align;
  // Ausrichtung (PARITAET-R3-8): Zeile mittig bzw. rechtsbündig im Satzspiegel
  const shift = (ln: Line) => {
    if (!align || !ln.parts.length) return 0;
    const used = Math.max(...ln.parts.map((pt) => pt.x + widthOf(pt.text, fontOf(pt.bold, pt.italic), st.size)));
    const free = Math.max(0, WIDTH - indent - used);
    return align === "center" ? free / 2 : free;
  };
  return lines.map((ln, i) => ({
    h: (i === 0 ? st.before : 0) + st.lead + (i === lines.length - 1 ? st.after : 0),
    // Überschriften nie allein am Seitenende; Absätze ohne Einzelzeile oben oder unten
    keep: b.kind.startsWith("h") || (lines.length > 1 && (i === 0 || i === lines.length - 2)),
    heading: b.kind.startsWith("h"),
    draw: (p, top) => {
      const base = top - (i === 0 ? st.before : 0) - st.size;
      if (i === 0 && b.kind === "li") p.text(L + indent - 16, base, b.marker, "F1", st.size);
      const dx = shift(ln);
      for (const part of ln.parts) drawPart(p, L + indent + dx + part.x, base, part, st.size);
    },
  }));
}

/**
 * Zeile unter einer Unterschrift. Auf Papier: das Datum auf dem Papier und wann die Agentur es erfasst hat; der
 * Erfassungszeitpunkt ist nie das Unterschriftsdatum (VERTRAEGE-02).
 */
export function signatureMeta(sig: Pick<ContractPdfSignature, "signingName" | "detail" | "signedAt" | "paper" | "paperSignedOn">,
  labels: { signedOn: string; paper: string; paperOn: string; recordedOn: string }): string {
  const when = sig.paper
    ? [sig.paperSignedOn ? `${labels.paperOn} ${sig.paperSignedOn}` : labels.paper, sig.signedAt ? `${labels.recordedOn} ${sig.signedAt}` : null]
    : [sig.signedAt ? `${labels.signedOn} ${sig.signedAt}` : null];
  return [sig.signingName, sig.detail, ...when].filter(Boolean).join(" · ");
}

function signatureBox(sig: ContractPdfSignature, imageIndex: number | null, labels: Required<NonNullable<ContractPdfModel["labels"]>>, mitProtokoll: boolean): Box {
  const meta = sig.signed ? signatureMeta(sig, labels) : sig.name ?? "";
  const metaLines = layoutRuns([{ text: meta, bold: false }], 8.5, 300 - 4);
  // R-28: unter einer elektronischen Unterschrift (gezeichnet oder getippt, nicht auf Papier) der Satz zum Nachweis
  const elektronisch = sig.signed && !sig.paper;
  const h = 18 + 58 + 8 + 12 + metaLines.length * 11 + (elektronisch ? 11 : 0) + 10;
  return {
    h, keep: false,
    draw: (p, top) => {
      const lineY = top - 18 - 58;
      if (sig.signed && imageIndex !== null && sig.image) {
        const scale = Math.min(200 / sig.image.width, 54 / sig.image.height, 1);
        p.image(imageIndex, L, lineY + 3, sig.image.width * scale, sig.image.height * scale);
      } else if (sig.signed) {
        // R-28: getippte Unterschrift im Schreibschrift-Stil: kursiv (Helvetica-Oblique) und größer als der Text, nicht fett
        p.text(L, lineY + 8, sig.signingName ?? "", "F3", 16, INK);
      } else {
        p.text(L, lineY + 8, labels.open, "F1", 9, MUTED);
      }
      p.line(L, lineY, L + 300, lineY, 0.7, MUTED);
      p.text(L, lineY - 12, sig.label, "F2", 9, INK);
      metaLines.forEach((ln, i) => { for (const part of ln.parts) p.text(L + part.x, lineY - 24 - i * 11, part.text, "F1", 8.5, MUTED); });
      if (elektronisch) p.text(L, lineY - 24 - metaLines.length * 11, `${labels.electronic}${mitProtokoll ? NACHWEIS_PLATZHALTER : ""}`, "F3", 8, MUTED);
    },
  };
}

/** Vertrag als PDF-Datei. `now` nur für reproduzierbare Tests. */
export function renderContractPdf(model: ContractPdfModel, opts: { now?: Date } = {}): Uint8Array {
  const { pages, images } = layoutContract(model);
  return assemble(pages, images, model.title, opts.now ?? new Date(), model.meta);
}

/** Seiten des Vertrags wie im PDF, für die Seitenansicht im Portal (PARITAET-R3-8). */
export interface ContractPagePreview {
  /** Seitengröße in Punkt (A4) */
  width: number;
  height: number;
  pages: ContractPageItem[][];
  images: ContractPdfImage[];
}
export function contractPagePreview(model: ContractPdfModel): ContractPagePreview {
  const { pages, images } = layoutContract(model);
  return { width: W, height: H, pages: pages.map((p) => p.items), images };
}

function layoutContract(model: ContractPdfModel): { pages: Page[]; images: ContractPdfImage[] } {
  const lang: Language = model.language === "en" ? "en" : "de";
  const labels = { ...DEFAULT_LABELS[lang], ...(model.labels ?? {}) } as Required<NonNullable<ContractPdfModel["labels"]>>;
  const images: ContractPdfImage[] = [];
  const boxes: Box[] = [];
  // PDF-Vorlage (0456): Logo, Farbe und Design, Schriftgröße, Seitenzahlen, zusätzliche Fußzeile
  const lay = belegLayout(model.layout);
  textSkala = BELEG_SCHRIFT_FAKTOR[lay.schrift];

  // Dokumentkopf: Logo der Agentur (Rahmen und Plattform tragen es nie; es gehört auf die Dokumente der Agentur)
  const logo = lay.logo.zeigen && model.logo && model.logo.width > 0 && model.logo.height > 0 ? model.logo : null;
  if (logo) {
    images.push(logo);
    const idx = images.length - 1;
    const box = belegLogoBox(lay.logo.groesse);
    const scale = Math.min(box.w / logo.width, box.h / logo.height);
    const w = logo.width * scale, h = logo.height * scale;
    const x = lay.logo.position === "rechts" ? R - w : L;
    boxes.push({ h: h + 14, keep: false, draw: (p, top) => p.image(idx, x, top - h, w, h) });
  }

  if (model.banner) {
    const lines = layoutRuns([{ text: model.banner, bold: true }], 10, WIDTH - 16);
    const h = lines.length * 13.6 + 16;
    boxes.push({ h: h + 8, keep: false, draw: (p, top) => {
      p.line(L, top, L, top - h, 2.5, WARN);
      lines.forEach((ln, i) => { for (const part of ln.parts) p.text(L + 10 + part.x, top - 10 - 10 - i * 13.6, part.text, "F2", 10, WARN); });
    } });
  }

  const addHtml = (html: string, breakBefore: boolean) => {
    const blocks = contractBlocks(html);
    const sigsUsed = new Map<ContractRole, number>();
    let first = true;
    // erzwungener Seitenumbruch: der nächste Kasten beginnt eine neue Seite
    let breakNext = false;
    const pushBoxes = (list: Box[]) => {
      if (breakNext && list[0]) { list[0].pageBreakBefore = true; breakNext = false; }
      boxes.push(...list);
    };
    for (let i = 0; i < blocks.length; i++) {
      const b = blocks[i]!;
      if (b.kind === "pagebreak") { breakNext = true; continue; }
      if (b.kind === "signature") {
        // je Unterzeichner dieser Partei ein Feld (mehrere Personen je Partei)
        const list = model.signatures.filter((s) => s.role === b.role).sort((a, c) => a.slot - c.slot);
        const shown = list.length ? list : [{ role: b.role, slot: 1, label: b.role, signed: false } as ContractPdfSignature];
        if (sigsUsed.has(b.role)) continue;
        sigsUsed.set(b.role, 1);
        // Schutz gegen allein stehende Unterschriftsfelder: die letzte Textzeile davor (über Überschriften hinweg) bleibt bei der
        // ersten Unterschrift
        for (let k = boxes.length - 1; k >= 0; k--) {
          boxes[k]!.keep = true;
          if (!boxes[k]!.heading && !boxes[k]!.spacer) break;
        }
        shown.forEach((s, k) => {
          let idx: number | null = null;
          if (s.signed && s.image) { images.push(s.image); idx = images.length - 1; }
          const box = signatureBox(s, idx, labels, !!model.protocol);
          const nextIsSig = k < shown.length - 1 || blocks[i + 1]?.kind === "signature";
          box.keep = nextIsSig;
          pushBoxes([box]);
        });
        continue;
      }
      if (b.kind === "table") {
        const list = tableBoxes(b);
        if (first && breakBefore && list[0]) list[0].pageBreakBefore = true;
        first = false;
        pushBoxes(list);
        continue;
      }
      const tb = textBoxes(b);
      if (first && breakBefore && tb[0]) tb[0].pageBreakBefore = true;
      first = false;
      pushBoxes(tb);
    }
  };
  addHtml(model.bodyHtml, false);
  if (model.annexHtml && model.annexHtml.replace(/<[^>]*>|\s|&nbsp;/g, "")) addHtml(model.annexHtml, true);

  if (model.figures && model.figures.items.length) {
    const fg = model.figures;
    boxes.push({ h: 24, keep: true, pageBreakBefore: true, draw: (p, top) => p.text(L, top - 14, fg.title, "F2", 13) });
    if (fg.intro) {
      for (const ln of layoutRuns([{ text: fg.intro, bold: false }], 8.5, WIDTH)) {
        boxes.push({ h: 11.5, keep: false, draw: (p, top) => { for (const part of ln.parts) p.text(L + part.x, top - 9, part.text, "F1", 8.5, MUTED); } });
      }
    }
    const maxImgH = TOP - BOTTOM - 90;
    for (const it of fg.items) {
      const cap = layoutRuns([{ text: it.caption, bold: true }], 10, WIDTH);
      const meta = (it.lines ?? []).flatMap((t) => layoutRuns([{ text: t, bold: false }], 8, WIDTH));
      let idx: number | null = null, iw = 0, ih = 0;
      if (it.image && it.image.width > 0 && it.image.height > 0) {
        images.push(it.image);
        idx = images.length - 1;
        const scale = Math.min(WIDTH / it.image.width, maxImgH / it.image.height, 1);
        iw = it.image.width * scale;
        ih = it.image.height * scale;
      }
      const textH = 10 + cap.length * 13 + meta.length * 10.5;
      boxes.push({
        h: textH + (idx !== null ? ih + 8 : 0) + 10, keep: false,
        draw: (p, top) => {
          let y = top - 10;
          cap.forEach((ln) => { y -= 13; for (const part of ln.parts) p.text(L + part.x, y + 3, part.text, "F2", 10, INK); });
          meta.forEach((ln) => { y -= 10.5; for (const part of ln.parts) p.text(L + part.x, y + 2, part.text, "F1", 8, MUTED); });
          if (idx !== null) p.image(idx, L, y - 6 - ih, iw, ih);
        },
      });
    }
  }

  // R-29: das Protokoll beginnt direkt unter dem Unterschriftenblock, wenn Titel, Einleitung, Prüfsummen, Kopfzeile und die
  // erste Zeile noch auf die Seite passen (Kette aus „keep“); sonst wie bisher auf einer neuen Seite. Die Seite merkt sich
  // der Kopf, damit der Satz unter den Unterschriften (R-28) sie nennen kann.
  const merker: { protokollSeite: Page | null } = { protokollSeite: null };
  if (model.protocol) {
    const pr = model.protocol;
    const head = layoutRuns([{ text: pr.title, bold: true }], 13, WIDTH);
    boxes.push({ h: 26, keep: true, spacer: true, draw: (p, top) => p.line(L, top - 10, R, top - 10, 0.4) });
    boxes.push({ h: 24, keep: true, draw: (p, top) => { merker.protokollSeite = p; p.text(L, top - 14, head[0]?.parts.map((x) => x.text).join(" ") ?? pr.title, "F2", 13); } });
    for (const para of [pr.intro, ...pr.hashes]) {
      for (const [i, ln] of layoutRuns([{ text: para, bold: false }], 8.5, WIDTH).entries()) {
        boxes.push({ h: 11.5 + (i === 0 ? 3 : 0), keep: true, draw: (p, top) => { for (const part of ln.parts) p.text(L + part.x, top - (i === 0 ? 3 : 0) - 9, part.text, "F1", 8.5, INK); } });
      }
    }
    const cols = [0, 110, 230, 350];
    const colW = [104, 114, 114, WIDTH - 350];
    const rowBox = (cells: string[], bold: boolean): Box => {
      const wrapped = cells.map((c, i) => layoutRuns([{ text: c, bold }], 8, colW[i]!));
      const nLines = Math.max(...wrapped.map((w) => w.length));
      return {
        h: nLines * 10.5 + 6, keep: bold,
        draw: (p, top) => {
          wrapped.forEach((w, ci) => w.forEach((ln, li) => { for (const part of ln.parts) p.text(L + cols[ci]! + part.x, top - 10 - li * 10.5, part.text, bold ? "F2" : "F1", 8, bold ? INK : INK); }));
          p.line(L, top - nLines * 10.5 - 4, R, top - nLines * 10.5 - 4, 0.4);
        },
      };
    };
    boxes.push({ h: 8, keep: true, draw: () => undefined });
    boxes.push(rowBox(pr.columns, true));
    for (const r of pr.rows) boxes.push(rowBox([r.at, r.event, r.person, r.evidence], false));
    if (pr.how) {
      boxes.push({ h: 20, keep: true, draw: (p, top) => p.text(L, top - 14, pr.how!.title, "F2", 9.5) });
      for (const ln of layoutRuns([{ text: pr.how.text, bold: false }], 8, WIDTH)) {
        boxes.push({ h: 10.5, keep: false, draw: (p, top) => { for (const part of ln.parts) p.text(L + part.x, top - 8, part.text, "F1", 8, MUTED); } });
      }
    }
  }

  // Umbruch: ein Kasten mit „keep“ bleibt beim nächsten; passt die Kette nicht mehr, beginnt eine neue Seite
  const pages: Page[] = [];
  let page = new Page();
  pages.push(page);
  let y = TOP;
  const usable = TOP - BOTTOM;
  for (let i = 0; i < boxes.length; i++) {
    const b = boxes[i]!;
    if (b.pageBreakBefore && y < TOP) { page = new Page(); pages.push(page); y = TOP; }
    // Tabellenzeile: auf einer neuen Seite stehen die Kopfzeilen davor, ihre Höhe zählt mit (Befund 6)
    const kopfHoehe = (b.repeat ?? []).reduce((a, k) => a + k.h, 0);
    let chain = b.h;
    for (let j = i; j < boxes.length - 1 && boxes[j]!.keep; j++) {
      if (boxes[j + 1]!.pageBreakBefore) break;
      chain += boxes[j + 1]!.h;
      if (chain > usable) break;
    }
    const need = Math.min(chain, usable - kopfHoehe);
    if (y - need < BOTTOM && y < TOP) {
      page = new Page(); pages.push(page); y = TOP;
      // Tabelle über die Seite: Kopfzeilen oben auf der neuen Seite wiederholen, sonst eine obere Rahmenlinie
      for (const k of b.repeat ?? []) { k.draw(page, y); y -= k.h; }
      if (!b.repeat?.length && b.lineOnBreak) page.line(L, y, R, y, 0.5);
    }
    b.draw(page, y);
    y -= b.h;
  }
  // R-28: Seitenzahl des Protokolls in den Satz unter den Unterschriften eintragen (gleiche Seite: „auf dieser Seite“)
  const protoIdx = merker.protokollSeite ? pages.indexOf(merker.protokollSeite) : -1;
  for (const [i, p] of pages.entries()) {
    const ersatz = protoIdx < 0 ? "" : i === protoIdx ? labels.electronicProofHere : labels.electronicProof(protoIdx + 1);
    const nachweisRegex = new RegExp(NACHWEIS_PLATZHALTER, "g");
    p.ops = p.ops.map((op) => (op.includes(NACHWEIS_PLATZHALTER) ? op.replace(nachweisRegex, ersatz) : op));
    for (const it of p.items) if (it.t === "text" && it.text.includes(NACHWEIS_PLATZHALTER)) it.text = it.text.replace(nachweisRegex, ersatz);
  }
  // Kopf- und Fußzeile
  // zu helle Farben bleiben neutral (grau wie ohne Farbe), nicht schwarz
  const farbe = lay.akzent ?? model.accent;
  const akzent = lay.design !== "minimal" && belegAkzent(farbe) ? pdfAccent(farbe) : null;
  textSkala = 1;
  pages.forEach((p, i) => {
    if (lay.design === "akzent" && akzent) p.line(0, H - 5, W, H - 5, 10, akzent);
    p.text(L, H - 40, model.header, "F1", 8, akzent ?? MUTED);
    // Vermerk über der Kopfzeile, rechtsbündig: bleibt frei von einer langen Kopfzeile und steht auf jeder Seite
    if (model.stamp) p.text(R - widthOf(model.stamp, "F2", 9), H - 27, model.stamp, "F2", 9, WARN);
    if (akzent) p.line(L, H - 46, R, H - 46, 0.6, akzent);
    else p.line(L, H - 46, R, H - 46, 0.4);
    p.line(L, 52, R, 52, 0.4);
    const pg = labels.page(i + 1, pages.length);
    if (lay.seitenzahlen) p.text(R - textWidth(pg, "F1", 8), 40, pg, "F1", 8, MUTED);
    p.text(L, 40, model.title.length > 80 ? `${model.title.slice(0, 79)}…` : model.title, "F1", 8, MUTED);
    if (lay.fusstext) p.text(L, 29, lay.fusstext.length > 110 ? `${lay.fusstext.slice(0, 109)}…` : lay.fusstext, "F1", 7.5, MUTED);
  });
  return { pages, images };
}
